import { Divider, Link, Menu, MenuItem, Typography } from '@mui/material';
import { getBroadcastChannel } from '../utils/broadcastChannelUtil';
import { FORBIDDEN_PATH, LOGOUT_BROADCAST_EVENT } from '../constants';
import { useAuth } from 'oidc-react';
import env from '../env';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import BugReportIcon from '@mui/icons-material/BugReport';
import PeopleIcon from '@mui/icons-material/People';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { useFeatureFlag } from '../provider/AppConfigProvider';

type AvatarMenuProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
};

const AvatarMenu = ({ anchorEl, open, onClose }: AvatarMenuProps) => {
  const auth = useAuth();
  const broadcastChannel = getBroadcastChannel();
  const almProfileUrl = `${env.ALM_BASE_URL}/v2/profile?clientId=${env.OIDC_CLIENT_ID}&referrerUrl=null`;
  const isIssueListEnabled = useFeatureFlag('issue-list', false);

  const handleUserLogOut = async () => {
    onClose();
    try {
      broadcastChannel.postMessage(LOGOUT_BROADCAST_EVENT); // Broadcast logout to other tabs
      const oidcState =
        window.location.pathname === FORBIDDEN_PATH
          ? undefined
          : {
              originalPath: window.location.href,
            };

      // Sign out in the current tab
      await auth.signOutRedirect({
        redirectMethod: 'replace',
        state: oidcState,
      });
    } catch (error) {
      console.info('Failed to Logout', error);
    }
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose} role="navigation">
        <MenuItem
          component={Link}
          href={almProfileUrl}
          target="_blank"
          aria-label={`Go to account settings`}
          onClick={onClose}
        >
          <ListItemIcon>
            <AccountCircleOutlinedIcon />
          </ListItemIcon>
          <Typography variant="body1">Profile</Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          component={Link}
          href={`/users`}
          aria-label={`Go to user list`}
          onClick={onClose}
        >
          <ListItemIcon>
            <PeopleIcon />
          </ListItemIcon>
          <Typography variant="body1">Users</Typography>
        </MenuItem>

        {isIssueListEnabled && (
          <MenuItem
            component={Link}
            href={`/issues`}
            aria-label={`Go to issue list`}
            onClick={onClose}
          >
            <ListItemIcon>
              <BugReportIcon />
            </ListItemIcon>
            <Typography variant="body1">Issus</Typography>
          </MenuItem>
        )}

        <MenuItem
          component={Link}
          href={`/`}
          aria-label={`Go to product list`}
          onClick={onClose}
        >
          <ListItemIcon>
            <LibraryBooksIcon />
          </ListItemIcon>
          <Typography variant="body1">Products</Typography>
        </MenuItem>

        <MenuItem
          onClick={handleUserLogOut}
          aria-label="Logout from application"
        >
          <ListItemIcon>
            <LogoutOutlinedIcon />
          </ListItemIcon>
          <Typography variant="body1">Log out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};

export default AvatarMenu;
